/**
 * ThemeContext - reads + writes <html data-theme="dark|light">.
 *
 * Scope: the dashboard SPA only. The auth shell (login/register/reset/
 * select-client) is dark-locked and never touches this provider.
 *
 * Three user-facing modes:
 *   'dark'   pin to dark
 *   'light'  pin to light
 *   'system' follow OS prefers-color-scheme (live - listens for OS
 *            theme changes and re-applies)
 *
 * The first paint is handled by the inline bootstrap in index.html
 * which sets <html data-theme> based on stored choice → OS pref →
 * dark. This provider trusts that for the initial value and takes
 * over from there.
 *
 * localStorage 'platform-ui:dashboard-theme' carries the MODE
 * (dark/light/system). The bootstrap reads it on next load and
 * resolves system → OS preference before first paint.
 *
 * Cross-tab sync: a storage listener picks up mode changes from
 * other tabs.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'dark' | 'light';
export type ThemeMode = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'platform-ui:dashboard-theme';
const SYSTEM_QUERY = '(prefers-color-scheme: light)';

interface ThemeContextValue {
  /** Resolved theme actually applied to <html data-theme>. */
  theme: Theme;
  /** User's mode preference. 'system' tracks the OS pref live. */
  mode: ThemeMode;
  setMode: (next: ThemeMode) => void;
  /** Toggle dark↔light. If currently 'system', toggles to the
   *  OPPOSITE of whatever system resolved to (so the toggle's
   *  observable behaviour matches what the user just saw). */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemTheme(): Theme {
  return typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia(SYSTEM_QUERY).matches
    ? 'light'
    : 'dark';
}

function readInitialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    /* fall through */
  }
  // No stored preference → trust whatever the bootstrap settled on.
  // Treat it as a hard pin so the user's first toggle behaves
  // intuitively (light ↔ dark) rather than flipping into 'system'.
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'light' ? 'light' : 'dark';
}

function resolveTheme(mode: ThemeMode): Theme {
  if (mode === 'system') return systemTheme();
  return mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode);
  const [theme, setTheme] = useState<Theme>(() => resolveTheme(readInitialMode()));

  // Apply mode to DOM + persist + recompute resolved theme.
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setTheme(resolved);
    document.documentElement.setAttribute('data-theme', resolved);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* private mode - the choice just won't persist. */
    }
  }, [mode]);

  // Live OS-preference listener - only matters in 'system' mode.
  useEffect(() => {
    if (mode !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(SYSTEM_QUERY);
    function onChange() {
      const next = systemTheme();
      setTheme(next);
      document.documentElement.setAttribute('data-theme', next);
    }
    // addEventListener works in evergreen browsers; ancient Safari
    // needs the deprecated addListener - guarded.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [mode]);

  // Cross-tab mode sync.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      const v = e.newValue;
      if (v === 'dark' || v === 'light' || v === 'system') {
        setModeState(v);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
  }, []);

  const toggle = useCallback(() => {
    // If currently following system, pin to the OPPOSITE of what
    // the user is seeing right now - they're clearly trying to
    // change what's displayed, not their mode preference.
    setModeState((m) => {
      if (m === 'system') {
        return systemTheme() === 'dark' ? 'light' : 'dark';
      }
      return m === 'dark' ? 'light' : 'dark';
    });
  }, []);

  const value: ThemeContextValue = { theme, mode, setMode, toggle };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
